import * as Sentry from '@sentry/browser'
import { DateTime } from 'luxon'
import type { DurationLike, DateObjectUnits } from 'luxon'
import { Service } from '@/services/helpers/ServiceHelper'

export const useDateTimeStore = defineStore('dateTime', () => {
  const { hasToForceEasternTz } = useFeatureFlags()
  const { formatDateTime } = useDateTimeFormat()
  const userStore = useUserStore()
  const threadStore = useThreadStore()
  const snackbar = useSnackbar()
  const tenantId = computed(() => userStore.currentTenantId)

  // STATE
  const availableFormats = ref({
    date: [
      { title: 'mm/dd/yyyy', value: 'LL/dd/yyyy' },
      { title: 'dd/mm/yyyy', value: 'dd/LL/yyyy' },
      { title: 'yyyy/mm/dd', value: 'yyyy/LL/dd' }
    ],
    time: [
      { title: '12-Hour (1:00 PM)', value: 't' },
      { title: '24-Hour (13:00)', value: 'T' }
    ]
  })
  const format = ref({
    date: localStorage.getItem('dateFormat') || availableFormats.value.date[0].value,
    time: localStorage.getItem('timeFormat') || availableFormats.value.time[0].value
  })
  const getTimezoneFriendly = (tz: string) => {
    if (!Intl.supportedValuesOf('timeZone').includes(tz)) {
      Sentry.captureException(new Error(`Invalid timezone: ${tz}`))
      return 'Invalid Timezone'
    }
    const dateTime = DateTime.local().setZone(tz)
    const offsetName = dateTime.offsetNameShort
    const city = tz.split('/').pop()!.replaceAll('_', ' ')
    return `${city} (${offsetName})`
  }
  const availableTimezones = ref(Intl.supportedValuesOf('timeZone')
    .map(tz => ({ title: getTimezoneFriendly(tz), value: tz }))
    .sort((a, b) => a.title.localeCompare(b.title)))
  const timezone = ref('America/New_York')
  const timezoneLocation = computed(() => getTimezoneFriendly(timezone.value))

  // GETTERS
  const getSystemTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone
  const getDateTimeNow = ({ timezone: tz, toFormat, offset = {} }: { timezone: string, toFormat?: string, offset?: DurationLike }) => {
    const date = DateTime.local().setZone(tz).plus(offset)
    return formatDateTime(date, toFormat)
  }
  const getUserDateTimeNow = ({ toFormat, offset = {} }: { toFormat?: string, offset?: DurationLike } = {}) => {
    const date = DateTime.local().setZone(timezone.value).plus(offset)
    return formatDateTime(date, toFormat)
  }
  const getUserDateTimeFromISO = ({ iso, toFormat, offset = {} }: { iso: string, toFormat?: string, offset?: DurationLike }) => {
    const date = DateTime.fromISO(iso, { zone: 'UTC' }).setZone(timezone.value).plus(offset) || null
    return formatDateTime(date, toFormat)
  }
  const getUserDateTimeFromJSDate = ({ jsDate, toFormat }: { jsDate: Date, toFormat?: string }) => {
    const systemTimezone = getSystemTimezone()
    const date = DateTime.fromJSDate(jsDate, { zone: systemTimezone }).setZone(timezone.value)
    return formatDateTime(date, toFormat)
  }
  const getUTCDateTimeNow = ({ toFormat, offset = {} }: { toFormat?: string, offset?: DurationLike }) => {
    const date = DateTime.utc().plus(offset)
    return formatDateTime(date, toFormat)
  }
  const getUTCDateTimeFromISO = ({ iso, toFormat, offset = {} }: { iso: string, toFormat?: string, offset?: DurationLike }) => {
    const date = DateTime.fromISO(iso, { zone: timezone.value }).setZone('UTC').plus(offset)
    return formatDateTime(date, toFormat)
  }
  const getUTCDateTimeFromJSDate = ({ jsDate, toFormat }: { jsDate: Date, toFormat?: string }) => {
    const systemTimezone = getSystemTimezone()
    const date = DateTime.fromJSDate(jsDate, { zone: systemTimezone }).setZone('UTC')
    return formatDateTime(date, toFormat)
  }
  const getUTCDateTimeFromObject = ({ object, toFormat }: { object: DateObjectUnits, toFormat?: string }) => {
    const date = DateTime.fromObject(object, { zone: 'UTC' })
    return formatDateTime(date, toFormat)
  }

  // ACTIONS
  const saveFormat = ({ date, time }: { date: string, time: string }) => {
    localStorage.setItem('dateFormat', date)
    localStorage.setItem('timeFormat', time)
    format.value = { date, time }
  }
  const initTimezone = async () => {
    if (hasToForceEasternTz.value) {
      await saveTimezone('America/New_York')
      return
    }
    try {
      const response = await Service?.gql?.loginSettingsGql?.currentLoginSettings(tenantId.value)
      timezone.value = response.timezone || getSystemTimezone()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to load timezone settings')
    }
  }
  const saveTimezone = async (newValue: string) => {
    try {
      const { success, errors } = await Service?.gql?.loginSettingsGql?.updateTimezone(tenantId.value, newValue)
      if (!success) {
        throw new Error(JSON.stringify(errors))
      }
      timezone.value = newValue
      threadStore.resetState()
      return true
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  }

  return {
    // STATE
    availableFormats,
    format,
    getTimezoneFriendly,
    availableTimezones,
    timezone,
    timezoneLocation,
    // GETTERS
    getSystemTimezone,
    getDateTimeNow,
    getUserDateTimeNow,
    getUserDateTimeFromISO,
    getUserDateTimeFromJSDate,
    getUTCDateTimeNow,
    getUTCDateTimeFromISO,
    getUTCDateTimeFromJSDate,
    getUTCDateTimeFromObject,
    // ACTIONS
    saveFormat,
    initTimezone,
    saveTimezone
  }
})
